import React from 'react';
import { format } from 'date-fns';
import toMoney from '../../functions/toMoney';
import BoletoBarcode from '../BoletoBarcode';
import { getBancoLogo } from '../../functions/bancoLogos';
import './style.css';

function formatDate(value) {
  return value ? format(new Date(`${value}T00:00:00`), 'dd/MM/yyyy') : '';
}

// Representação impressa do boleto (CA07), no layout padrão de ficha de
// compensação — Recibo do Pagador (canhoto) + Ficha de Compensação — a
// partir do `boleto` já recebido na listagem — nenhum campo aqui é
// recalculado. Segue a mesma grade de um boleto bancário real: os rótulos
// aparecem sempre, mesmo sem valor, porque é assim que o documento
// original é impresso.
function BoletoPrint({ titulo }) {
  const boleto = titulo?.boleto || {};
  // `beneficiario_boleto`/`pagador_boleto` agora vêm uma vez por contrato,
  // anexados ao título fora daqui (ver Dashboard/table.js). Mantém o fallback
  // pro shape antigo (dentro de `boleto`) por segurança durante a transição.
  const beneficiario = titulo?.beneficiario_boleto || boleto.beneficiario;
  const pagador = titulo?.pagador_boleto || boleto.pagador;
  const valor = boleto.valor_documento ?? titulo?.valor_titulo;
  const vencimento = boleto.data_vencimento || titulo?.vencto;
  const agenciaConta = [
    [boleto.agencia, boleto.dv_agencia].filter(Boolean).join('-'),
    [boleto.conta, boleto.dv_conta].filter(Boolean).join('-'),
  ]
    .filter(Boolean)
    .join(' / ');
  const nossoNumero = [boleto.nosso_numero, boleto.dv_nosso_numero]
    .filter(Boolean)
    .join('-');
  // O "-X" ao lado do código do banco num boleto real é um dígito
  // verificador próprio do código do banco (ex.: 104-0 na Caixa,
  // 237-2 no Bradesco) — não é um valor fixo, e a api-charge não manda
  // esse campo. Mostrar só o código evita exibir um dígito inventado.
  const bancoLabel = boleto.codigo_banco || '';
  const logo = getBancoLogo(boleto.codigo_banco);
  const bancoHeader = logo ? (
    <img
      className="boleto-print-logo"
      src={logo.mark}
      alt={boleto.nome_banco || 'Banco'}
    />
  ) : (
    <div className="boleto-print-banco">{boleto.nome_banco || 'Banco'}</div>
  );

  return (
    <div className="boleto-print">
      {/* Recibo do Pagador (canhoto) — resumo que o pagador guarda. */}
      <div className="boleto-print-top">
        {bancoHeader}
        <div className="boleto-print-codigo">{bancoLabel}</div>
        <div className="boleto-print-linha">{boleto.linha_digitavel}</div>
        <div className="boleto-print-recibo-label">Recibo do pagador</div>
      </div>

      <div className="boleto-print-grid boleto-print-grid--r1">
        <div>
          <span>Pagador</span>
          <strong className="boleto-print-sm">{pagador?.nome || '-'}</strong>
        </div>
        <div>
          <span>CPF/CNPJ do pagador</span>
          <strong>{pagador?.cpf_cnpj || '-'}</strong>
        </div>
      </div>

      <div className="boleto-print-grid boleto-print-grid--r2">
        <div>
          <span>Nosso número</span>
          <strong>{nossoNumero || '-'}</strong>
        </div>
        <div>
          <span>Nº documento</span>
          <strong>{boleto.numero_documento || '-'}</strong>
        </div>
        <div>
          <span>Agência/Cód. beneficiário</span>
          <strong>{agenciaConta || '-'}</strong>
        </div>
        <div>
          <span>Vencimento</span>
          <strong>{formatDate(vencimento)}</strong>
        </div>
        <div>
          <span>(=) Valor do documento</span>
          <strong>R$ {toMoney(valor)}</strong>
        </div>
      </div>

      <div className="boleto-print-grid boleto-print-grid--r1">
        <div>
          <span>Beneficiário</span>
          <strong className="boleto-print-sm">
            {beneficiario?.nome || '-'}
            {beneficiario?.endereco ? ` — ${beneficiario.endereco}` : ''}
          </strong>
        </div>
        <div>
          <span>CPF/CNPJ do beneficiário</span>
          <strong>{beneficiario?.cnpj || '-'}</strong>
        </div>
      </div>

      <div className="boleto-print-auth">Autenticação mecânica</div>

      <div className="boleto-print-cut">
        <span>Recibo do pagador</span>
      </div>

      {/* Ficha de Compensação — via do boleto que circula no sistema bancário. */}
      <div className="boleto-print-top">
        {bancoHeader}
        <div className="boleto-print-codigo">{bancoLabel}</div>
        <div className="boleto-print-linha">{boleto.linha_digitavel}</div>
      </div>

      <div className="boleto-print-grid boleto-print-grid--r1">
        <div>
          <span>Local de pagamento</span>
          <strong className="boleto-print-sm">
            {boleto.local_pagamento ||
              'Pagável em qualquer banco, lotérica ou aplicativo até o vencimento.'}
          </strong>
        </div>
        <div>
          <span>Vencimento</span>
          <strong>{formatDate(vencimento)}</strong>
        </div>
      </div>

      <div className="boleto-print-grid boleto-print-grid--r1">
        <div>
          <span>Beneficiário</span>
          <strong className="boleto-print-sm">
            {beneficiario?.nome || '-'}
            {beneficiario?.cnpj ? ` — CNPJ ${beneficiario.cnpj}` : ''}
          </strong>
        </div>
        <div>
          <span>Agência / Código do beneficiário</span>
          <strong>{agenciaConta || '-'}</strong>
        </div>
      </div>

      <div className="boleto-print-grid boleto-print-grid--r2">
        <div>
          <span>Data do documento</span>
          <strong>{formatDate(boleto.data_documento)}</strong>
        </div>
        <div>
          <span>Nº documento</span>
          <strong>{boleto.numero_documento || '-'}</strong>
        </div>
        <div>
          <span>Espécie doc.</span>
          <strong>{boleto.especie_documento || '-'}</strong>
        </div>
        <div>
          <span>Aceite</span>
          <strong>{boleto.aceite || 'N'}</strong>
        </div>
        <div>
          <span>Nosso número</span>
          <strong>{nossoNumero || '-'}</strong>
        </div>
      </div>

      <div className="boleto-print-grid boleto-print-grid--r3">
        <div>
          <span>Uso do banco</span>
          <strong>&nbsp;</strong>
        </div>
        <div>
          <span>Carteira</span>
          <strong>{boleto.carteira || '-'}</strong>
        </div>
        <div>
          <span>Espécie</span>
          <strong>R$</strong>
        </div>
        <div>
          <span>Quantidade</span>
          <strong>&nbsp;</strong>
        </div>
        <div>
          <span>(-) Desconto / Abatimento</span>
          <strong>{boleto.desconto ? `R$ ${toMoney(boleto.desconto)}` : ''}</strong>
        </div>
        <div>
          <span>(=) Valor do documento</span>
          <strong>R$ {toMoney(valor)}</strong>
        </div>
      </div>

      {!!(boleto.instrucoes || []).length && (
        <div className="boleto-print-instrucoes">
          <span>Instruções (texto de responsabilidade do beneficiário)</span>
          <ul>
            {boleto.instrucoes.map((linha, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`instrucao-${index}`}>{linha}</li>
            ))}
          </ul>
        </div>
      )}

      {pagador && (
        <div className="boleto-print-pagador">
          <strong>Pagador:</strong> {pagador.nome}
          {pagador.cpf_cnpj ? ` — CPF/CNPJ ${pagador.cpf_cnpj}` : ''}
          {pagador.endereco && <div>{pagador.endereco}</div>}
        </div>
      )}

      <div className="boleto-print-barcode-wrapper">
        <BoletoBarcode codigoBarras={boleto.codigo_de_barras} />
      </div>

      <div className="boleto-print-auth">
        Autenticação mecânica — Ficha de Compensação
      </div>
    </div>
  );
}

export default BoletoPrint;
